<template>
  <div ref="outer" class="w-full h-full">
    <el-scrollbar
      v-if="showScrollbar && scrollbarHeight > 0"
      :style="{ height: scrollbarHeight + 'px' }"
    >
      <div
        v-infinite-scroll="handleLoad"
        class="flex flex-col"
        :infinite-scroll-disabled="disabled"
        :infinite-scroll-distance="distance"
      >
        <div
          v-for="(item, index) in items"
          :key="JSON.stringify(item) ?? `cursor-infinite-item-${index}`"
          :class="itemClass"
        >
          <slot :item="item" :index="index">
            <div class="font-semibold text-[var(--el-color-primary,#409eff)]">
              {{ item.rule_name }}
            </div>
          </slot>
        </div>
        <div
          v-if="loading"
          class="flex items-center justify-center gap-2 text-[var(--el-text-color-secondary,#909399)] py-3"
        >
          <el-icon class="is-loading"><i class="icon-loading" /></el-icon>
          <span>{{ loadingText }}</span>
        </div>
        <div
          v-else-if="!hasMore && items.length"
          class="flex items-center justify-center gap-2 text-xs text-[var(--el-text-color-secondary,#909399)] py-3"
        >
          {{ finishedText }}
        </div>
        <el-empty
          v-else-if="!items.length && !loading"
          :description="emptyText"
        />
      </div>
    </el-scrollbar>
  </div>
</template>

<script>
export default {
  name: "SmartInfiniteCursorScroll",
  props: {
    loadMethod: { type: Function, required: true },
    pageSize: { type: Number, default: 20 },
    height: { type: Number, default: 320 },
    distance: { type: Number, default: 50 },
    loadingText: { type: String, default: "Loading..." },
    finishedText: { type: String, default: "No more data" },
    emptyText: { type: String, default: "No data" },
    itemClass: { type: String, default: "" },
    listField: { type: String, default: "list" },
    queryParams: { type: Object, default: undefined },
    queryType: { type: String, default: "server" },
    localList: { type: Array, default: undefined },
  },
  data() {
    return {
      allLocalItems: [],
      items: [],
      cursor: "",
      hasMore: true,
      loading: false,
      nextCursor: "",
      queryParamsData: { ...(this.queryParams || {}) },
      scrollbarHeight: 0,
      showScrollbar: true,
    };
  },
  computed: {
    disabled() {
      return this.loading || !this.hasMore || !this.loadMethod;
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.outer) {
        this.scrollbarHeight = this.$refs.outer.offsetHeight;
      }
    });
  },
  watch: {
    queryParams: {
      handler(val) {
        if (val && typeof val === "object") {
          this.queryParamsData = { ...val };
          this.refresh();
        }
      },
      deep: true,
    },
  },
  methods: {
    setQueryParams(params) {
      this.queryParamsData = { ...params };
    },
    async fetchNextPage() {
      if (this.loading || !this.hasMore) return;
      this.loading = true;
      try {
        if (this.queryType === "local") {
          if (!this.allLocalItems.length && Array.isArray(this.localList)) {
            this.allLocalItems = [...this.localList];
          }
          let filtered = this.allLocalItems;
          if (
            this.queryParamsData &&
            Object.keys(this.queryParamsData).length
          ) {
            filtered = this.allLocalItems.filter((item) => {
              return Object.entries(this.queryParamsData).every(([k, v]) => {
                if (v === undefined || v === null || v === "") return true;
                return String(item[k] ?? "").includes(String(v));
              });
            });
          }
          const start = this.items.length;
          const end = start + this.pageSize;
          const page = filtered.slice(start, end);
          this.items = [...this.items, ...page];
          this.hasMore = end < filtered.length;
        } else {
          if (!this.loadMethod) return;
          const params = {
            ...this.queryParamsData,
            cursor: this.cursor,
            page_size: this.pageSize,
          };
          const res = await this.loadMethod(params);
          const list = res[this.listField] || [];
          this.items = [...this.items, ...list];
          this.nextCursor = typeof res.next_cursor === 'string' ? res.next_cursor : '';
          this.cursor = this.nextCursor;
          // 链表分页：nextCursor 为空字符串时视为到底
          this.hasMore = !!this.nextCursor;
        }
      } finally {
        this.loading = false;
      }
    },
    async handleLoad() {
      if (this.disabled) return;
      await this.fetchNextPage();
    },
    refresh(params) {
      this.showScrollbar = false;
      this.items = [];
      this.cursor = "";
      this.hasMore = true;
      this.nextCursor = "";
      if (this.queryType === "local") {
        this.allLocalItems = Array.isArray(this.localList)
          ? [...this.localList]
          : [];
      }
      if (params) this.setQueryParams(params);
      this.$nextTick(() => {
        this.showScrollbar = true;
      });
    },
  },
};
</script>
